import { Slider } from 'antd';
import React, { useEffect, useState } from 'react';
import { AiFillCheckCircle } from 'react-icons/ai';
import { useDispatch } from 'react-redux';
import Amortisation from '../../../../../../assets/images/deals/Amortisation.svg';
import Bullet from '../../../../../../assets/images/deals/Bullet.svg';
import NormalInput from '../../../../../common/NormalInput';
import { commaSeperator } from '../../../../../utils/validation';
import DealActions from '../../../redux/actions';
import { CashFlowSimulator } from '../../../redux/types';

type Props = {
    dealName?: string;
    dealUUID?: string;
    open: boolean;
    cashflowSimulator?: CashFlowSimulator;
};

const CashflowSimulatorCalculator: React.FC<Props> = ({ dealName, dealUUID, open, cashflowSimulator }) => {
    const dispatch = useDispatch();

    const [amountValue, setAmountValue] = useState(10000000);

    const onChangeAmount = (newValue: number) => {
        setAmountValue(newValue);
    };

    useEffect(() => {
        if (open && dealUUID) {
            dispatch(
                DealActions.fetchCashflowSimulator({
                    amount: {
                        amountUpto2dp: 10000000,
                        currency: 'SGD',
                    },
                    dealUUID: dealUUID,
                }),
            );
        }
    }, [open, dispatch, dealUUID]);

    const calculate = () => {
        if (dealUUID) {
            dispatch(
                DealActions.fetchCashflowSimulator({
                    amount: {
                        amountUpto2dp: amountValue,
                        currency: 'SGD',
                    },
                    dealUUID: dealUUID,
                }),
            );
        }
    };

    const reset = () => {
        if (dealUUID) {
            setAmountValue(10000000);
            dispatch(
                DealActions.fetchCashflowSimulator({
                    amount: {
                        amountUpto2dp: 10000000,
                        currency: 'SGD',
                    },
                    dealUUID: dealUUID,
                }),
            );
        }
    };

    return (
        <div className="w-full md:w-[35%] lg:w-[30%] bg-[#EFEFEF] py-4 flex flex-col justify-center items-center">
            <p className="text-[#20222B] flex md:hidden relative top-14 px-6  font-[WorkSans-Semibold] font-semibold text-lg  text-center">
                See how {dealName} can work for you
            </p>
            <div
                className="bg-white px-4 py-4 w-4/5  md:w-full relative top-20 md:top-0 md:-right-8"
                style={{ boxShadow: '5px 5px 30px 0px #0000001A' }}
            >
                <div>
                    <p className="text-[#5C5C5C] font-[WorkSans] text-center text-sm ">
                        Amount ({cashflowSimulator?.currency})
                    </p>
                    <NormalInput
                        className="border-b-0"
                        name="amountValue"
                        value={amountValue ? commaSeperator(String(amountValue)) : undefined}
                        onChange={(data) => {
                            setAmountValue(
                                data.value && data.value !== '' ? parseInt(String(data.value).replaceAll(',', '')) : 1,
                            );
                        }}
                    />
                    <Slider
                        onChange={onChangeAmount}
                        value={typeof amountValue === 'number' ? amountValue : 0}
                        min={1}
                        className="relative -top-4"
                        max={10000000}
                        trackStyle={{
                            backgroundColor: '#13974B',
                        }}
                        handleStyle={{
                            backgroundColor: '#13974B',
                        }}
                        tooltip={{
                            open: false,
                        }}
                        marks={{
                            0: {
                                style: {
                                    fontSize: 8,
                                    fontFamily: 'WorkSans',
                                    color: '#8A8A8A',
                                },
                                label: 1,
                            },
                            10000000: {
                                style: {
                                    fontSize: '8px',
                                    fontFamily: 'WorkSans',
                                    color: '#8A8A8A',
                                    position: 'relative',
                                    left: '96%',
                                },
                                label: '10,000,000',
                            },
                        }}
                    />
                </div>
                <div className="-mt-4">
                    <p className="text-[#5C5C5C] font-[WorkSans] text-center text-sm">Duration (Months)</p>
                    <NormalInput
                        value={cashflowSimulator?.durationInMonths}
                        name="duration"
                        disabled
                        onChange={() => {}}
                    />
                </div>
                <div className="mt-4">
                    <p className="text-[#5C5C5C] font-[WorkSans] text-center text-sm">Interest Rate (% p.a.)</p>
                    <NormalInput value={cashflowSimulator?.interestRate} name="duration" disabled onChange={() => {}} />
                </div>
                <div className="mt-4">
                    <p className="text-[#5C5C5C] font-[WorkSans] text-center text-sm">Repayment Type</p>
                    <div className="flex justify-around items-center pt-4">
                        <div>
                            <div className="bg-[#f5f5f5] h-20 w-20 flex justify-center items-center">
                                <img src={Amortisation} alt="Amortisation" />
                                {cashflowSimulator?.repaymentType === 'Amortisation' ? (
                                    <AiFillCheckCircle className="absolute -bottom-2" size={'1.3rem'} color="#8A8A8A" />
                                ) : null}
                            </div>
                            <p className="textt-[#20222B] pt-2 text-center font-[WorkSans-Semibold] text-xs">
                                Amortisation
                            </p>
                        </div>
                        <div>
                            <div className="bg-[#f5f5f5] h-20 w-20 flex justify-center items-center relative">
                                <img src={Bullet} alt="Bullet" />
                                {cashflowSimulator?.repaymentType === 'Bullet' ? (
                                    <AiFillCheckCircle className="absolute -bottom-2" size={'1.3rem'} color="#8A8A8A" />
                                ) : null}
                            </div>
                            <p className="textt-[#20222B] pt-2 text-center font-[WorkSans-Semibold] text-xs">Bullet</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 mt-8">
                        <div onClick={reset} className="py-3 px-5 lg:px-8 text-[#20222B] cursor-pointer">
                            <p className="font-[WorkSans-Semibold] font-bold text-xs lg:text-base text-center">Reset</p>
                        </div>
                        <div onClick={calculate} className="py-3 px-5 lg:px-8 bg-[#13974B] text-white cursor-pointer">
                            <p className="font-[WorkSans-Semibold] font-bold text-xs lg:text-base text-center">
                                Calculate
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CashflowSimulatorCalculator;
